import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { GameView } from '../shared/types';
import { AUDIO_STORAGE_KEY, GameAudioCursor, PartyAudio, parseAudioPreferences, type AudioPreferences, type AudioStatus } from './audio';
import './SoundControls.css';

export interface GameAudioControls {
  preferences: AudioPreferences;
  status: AudioStatus;
  setBgm(enabled: boolean): void;
  setSfx(enabled: boolean): void;
  setVolume(volume: number): void;
}

function readPreferences(): AudioPreferences {
  try { return parseAudioPreferences(localStorage.getItem(AUDIO_STORAGE_KEY)); }
  catch { return parseAudioPreferences(null); }
}

export function useGameAudio(game?: GameView, playerId?: string, connected = true): GameAudioControls {
  const [preferences, setPreferences] = useState(readPreferences);
  const [status, setStatus] = useState<AudioStatus>('locked');
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState !== 'hidden');
  const preferencesRef = useRef(preferences);
  const engine = useRef<PartyAudio | undefined>(undefined);
  const cursor = useRef(new GameAudioCursor());

  useEffect(() => {
    const audio = new PartyAudio(setStatus);
    engine.current = audio;
    audio.configure(preferencesRef.current);
    audio.setVisible(document.visibilityState !== 'hidden');
    const visibility = () => { const next = document.visibilityState !== 'hidden'; setVisible(next); audio.setVisible(next); };
    const gesture = (event: Event) => {
      if (!event.isTrusted) return;
      if (event instanceof KeyboardEvent && (event.ctrlKey || event.metaKey || event.altKey)) return;
      if (preferencesRef.current.bgm || preferencesRef.current.sfx) void audio.unlock();
    };
    document.addEventListener('visibilitychange', visibility);
    document.addEventListener('click', gesture);
    document.addEventListener('keydown', gesture);
    return () => {
      document.removeEventListener('visibilitychange', visibility);
      document.removeEventListener('click', gesture);
      document.removeEventListener('keydown', gesture);
      audio.dispose();
      if (engine.current === audio) engine.current = undefined;
    };
  }, []);

  useEffect(() => {
    const cues = cursor.current.observe(game, playerId, connected && visible, preferences.sfx && status === 'ready');
    if (cues.length) engine.current?.play(cues);
  }, [game, playerId, connected, visible, preferences.sfx, status]);

  const update = useCallback((patch: Partial<AudioPreferences>) => {
    const next = { ...preferencesRef.current, ...patch };
    preferencesRef.current = next; setPreferences(next);
    try { localStorage.setItem(AUDIO_STORAGE_KEY, JSON.stringify(next)); } catch { /* Storage is optional in private browsing. */ }
    engine.current?.configure(next);
    if (next.bgm || next.sfx) void engine.current?.unlock();
  }, []);
  const setBgm = useCallback((bgm: boolean) => update({ bgm }), [update]);
  const setSfx = useCallback((sfx: boolean) => update({ sfx }), [update]);
  const setVolume = useCallback((volume: number) => { if (Number.isFinite(volume)) update({ volume: Math.min(1, Math.max(0, volume)) }); }, [update]);
  return { preferences, status, setBgm, setSfx, setVolume };
}

function SoundIcon({ muted }: { muted: boolean }) {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4V5Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />{muted ? <path d="m16 9 5 6m0-6-5 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /> : <><path d="M15 8a6 6 0 0 1 0 8M18 5a10 10 0 0 1 0 14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></>}</svg>;
}

export function SoundControls({ audio }: { audio: GameAudioControls }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const firstToggle = useRef<HTMLButtonElement>(null);
  const id = useId();
  const { preferences, status } = audio;
  const enabled = preferences.bgm || preferences.sfx;
  useEffect(() => {
    if (!open) return;
    firstToggle.current?.focus();
    const outside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.preventDefault(); setOpen(false); trigger.current?.focus(); } };
    document.addEventListener('pointerdown', outside); document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [open]);
  const description = status === 'unsupported' ? '이 브라우저에서는 소리를 사용할 수 없어요.'
    : enabled && status === 'locked' ? '화면을 한 번 누르면 소리가 시작돼요.'
      : preferences.bgm && status === 'ready' ? '배경 음악 재생 중 · 다른 탭에서는 일시 정지'
        : '다른 탭으로 이동하면 소리가 일시 정지됩니다.';
  return <div className="sound-controls" ref={root} onBlur={event => { if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false); }}>
    <button className={`sound-trigger ${enabled && preferences.volume > 0 ? 'is-enabled' : ''}`} type="button" ref={trigger} onClick={() => setOpen(value => !value)} onKeyDown={event => { if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); } }} aria-label="소리 설정" aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? `${id}-panel` : undefined} title={`배경음 ${preferences.bgm ? '켜짐' : '꺼짐'} · 효과음 ${preferences.sfx ? '켜짐' : '꺼짐'}`}>
      <SoundIcon muted={!enabled || preferences.volume === 0} /><span>소리</span><small>BGM {preferences.bgm ? 'ON' : 'OFF'}</small>
    </button>
    {open && <section className="sound-panel" id={`${id}-panel`} role="dialog" aria-label="소리 설정" aria-describedby={`${id}-note`}>
      <div className="sound-panel-heading"><div><h2>소리 설정</h2><p>음악과 효과음을 따로 켤 수 있습니다.</p></div><button className="sound-close" type="button" onClick={() => { setOpen(false); trigger.current?.focus(); }} aria-label="소리 설정 닫기">×</button></div>
      <div className="sound-option"><div><label id={`${id}-bgm`}>배경 음악</label><span>잔잔한 연주곡 · 반복 재생</span></div><button className="sound-switch" type="button" role="switch" ref={firstToggle} aria-checked={preferences.bgm} aria-labelledby={`${id}-bgm`} onClick={() => audio.setBgm(!preferences.bgm)}><span aria-hidden="true" /><b>{preferences.bgm ? '켜짐' : '꺼짐'}</b></button></div>
      <div className="sound-option"><div><label id={`${id}-sfx`}>게임 효과음</label><span>던지기 · 이동 · 잡기 · 내 차례</span></div><button className="sound-switch" type="button" role="switch" aria-checked={preferences.sfx} aria-labelledby={`${id}-sfx`} onClick={() => audio.setSfx(!preferences.sfx)}><span aria-hidden="true" /><b>{preferences.sfx ? '켜짐' : '꺼짐'}</b></button></div>
      <div className="sound-volume"><div><label htmlFor={`${id}-volume`}>전체 볼륨</label><output htmlFor={`${id}-volume`}>{Math.round(preferences.volume * 100)}%</output></div><input id={`${id}-volume`} type="range" min={0} max={100} step={1} value={Math.round(preferences.volume * 100)} onChange={event => audio.setVolume(Number(event.target.value) / 100)} aria-valuetext={`${Math.round(preferences.volume * 100)}퍼센트`} /></div>
      <p className="sound-note" id={`${id}-note`} role="status">{description}</p>
    </section>}
  </div>;
}
